import { useCallback, useRef } from "react";
import { API_URL } from "../lib/utils";
import { useAuth } from "../contexts/AuthContext";

export const useApi = () => {
    const { token } = useAuth();
    const tokenRef = useRef(token);
    tokenRef.current = token;

    const request = useCallback(
        async (endpoint, options = {}) => {
            const currentToken = tokenRef.current;
            const headers = {
                "Content-Type": "application/json",
                ...options.headers,
            };

            if (currentToken && currentToken !== "oauth_session") {
                headers.Authorization = `Bearer ${currentToken}`;
            }

            let lastError;
            for (let attempt = 0; attempt < 2; attempt++) {
                try {
                    const response = await fetch(`${API_URL}${endpoint}`, {
                        ...options,
                        headers,
                        credentials: "include",
                    });

                    if (!response.ok) {
                        const errorData = await response.json().catch(() => ({}));
                        throw new Error(errorData.detail || `Request failed with status ${response.status}`);
                    }

                    return await response.json();
                } catch (err) {
                    lastError = err;
                    if (attempt === 0 && !err.message?.includes("status 4")) {
                        await new Promise(r => setTimeout(r, 800));
                    }
                }
            }
            throw lastError;
        },
        // eslint-disable-next-line react-hooks/exhaustive-deps
        []
    );

    const get = useCallback((endpoint) => request(endpoint), [request]);

    const post = useCallback(
        (endpoint, body) =>
            request(endpoint, {
                method: "POST",
                body: JSON.stringify(body),
            }),
        [request]
    );

    const put = useCallback(
        (endpoint, body) =>
            request(endpoint, {
                method: "PUT",
                body: JSON.stringify(body),
            }),
        [request]
    );

    const del = useCallback(
        (endpoint) =>
            request(endpoint, {
                method: "DELETE",
            }),
        [request]
    );

    return { get, post, put, del };
};
